/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { StyleSheet, css } from 'aphrodite'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  Tooltip,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Alert from '@material-ui/lab/Alert'
import CloseIcon from '@material-ui/icons/Close'
import WhatsAppIcon from '@material-ui/icons/WhatsApp'
import EditIcon from '@material-ui/icons/Edit'

import { Link } from 'react-router-dom'
import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { AppHelper } from '../../../helpers/AppHelper'
import { AppUiHelper } from '../../../helpers/AppUiHelper'
import { AppConfig } from '../../../AppConfig'
import { AppStyles, AppTheme } from '../../../styles/AppTheme'
import { CustomDynamicFormStyles } from '../../../styles/CustomStyles'
import { AppButton } from '../../../common/components/AppButton'
import { WhatsappTemplatePreview } from '../../../common/components/WhatsappTemplatePreview'
import { DocumentUploader } from '../../../common/components/DocumentUploader'
import { ImageUploaderWithAssetPicker } from '../../../components/ImageUploaderWithAssetPicker'
import { buildPreviewComponentsFromTemplate } from './WhatsappPreviewAdapter'
import { cachedWaTemplatePreview } from './whatsapp-chatbot/chatbotTemplatePreview'
import {
  normalizeWaComponentsMap,
  buildWaComponentsFromBindings,
  getWaMappingSlots,
  areAllWaMappingSlotsFilled,
  remapWaComponentsToTemplate,
  getWaMappingHealth,
  waComponentsMapsEqual,
  getCrmNumbersRoute,
} from './WorkflowHelper'

const WA_MEDIA_ASSET_PICKER_Z = 140000
const WA_IMAGE_ALLOWED_FORMATS = ['png', 'jpeg', 'jpg']

const FormStyles = AppConfig.CUSTOM_STYLE ? CustomDynamicFormStyles : StyleSheet.create({
  formControl: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  autoCompleteInputRoot: {
    paddingRight: 56,
  },
  popupIndicator: {
    marginRight: 5,
  },
  clearIndicatorAlwaysVisible: {
    visibility: 'visible',
    opacity: 1,
    pointerEvents: 'auto',
  },
  clearIndicatorHidden: {
    display: 'none !important',
  },
  dialog: {
    zIndex: 130000,
    fontSize: 16,
    color: '#222222',
  },
  option: {
    fontSize: 16,
    color: '#222222',
  },
  labelContainer: {
    display: 'flex',
    marginBottom: 8,
  },
})

function contactVariableOptions(vars) {
  const list = Array.isArray(vars) && vars.length > 0
    ? vars
    : AppUiHelper.getContactTemplateVariables()
  return list.map((v) => ({
    value: v.value || v.key,
    label: v.text || v.label || v.value || v.key,
  }))
}

function isMissingDefaultWaNumberError(message) {
  return String(message || '').toLowerCase().includes('account default whatsapp number is not configured')
}

function isWhatsAppCrmNumbersEmpty(crmNumbersResult) {
  if (crmNumbersResult == null) return true
  const items = Array.isArray(crmNumbersResult.items)
    ? crmNumbersResult.items
    : (Array.isArray(crmNumbersResult) ? crmNumbersResult : null)
  if (items == null) return true
  return items.length === 0
}

function buildUnmappedTemplateKeyReason(key) {
  return `WhatsApp template key '${key}' is not mapped to a template`
}

function templateKeyOptionsFromSettings(orgId, currentKey) {
  const raw = AppHelper.getSetting(orgId, 'WA_TEMPLATE_TAGS') || ''
  const opts = []
  const seen = new Set()
  String(raw).split(',').forEach((part) => {
    const key = String(part || '').trim()
    if (!key || key === 'NONE' || key === 'none' || seen.has(key)) return
    seen.add(key)
    opts.push({ key, label: key, text: key })
  })
  if (currentKey && !seen.has(currentKey)) {
    opts.unshift({ key: currentKey, label: currentKey, text: currentKey })
  }
  return opts
}

function normalizeValue(value) {
  const v = value || {}
  const comps = (v.waComponents && typeof v.waComponents === 'object' && !Array.isArray(v.waComponents))
    ? v.waComponents
    : {}
  return {
    waTemplateKey: (v.waTemplateKey || '').trim(),
    waComponents: comps,
  }
}

export function WaTemplateKeyDialog(props) {
  const {
    open,
    onClose,
    orgId,
    onSave,
    showToast,
    title,
    subtitle,
    previewByKey,
    templateVariables: templateVariablesProp,
  } = props
  const value = normalizeValue(props.value)

  const [isLoading, setIsLoading] = useState(false)
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('')
  const [waTemplate, setWaTemplate] = useState(null)
  const [waComponents, setWaComponents] = useState({})
  const [templateVariables, setTemplateVariables] = useState(() => contactVariableOptions(templateVariablesProp))
  const [hydrateError, setHydrateError] = useState(null)
  const [prereqWarning, setPrereqWarning] = useState(null)
  const [isHydrating, setIsHydrating] = useState(false)
  const [mappingsDirty, setMappingsDirty] = useState(false)
  const [waCrmNumberMissing, setWaCrmNumberMissing] = useState(false)
  const [editingVar, setEditingVar] = useState(null)
  const [varRadioValue, setVarRadioValue] = useState('')
  const [varCustomText, setVarCustomText] = useState('')
  const [varMediaLink, setVarMediaLink] = useState('')
  const [varMedia, setVarMedia] = useState('')
  const [varFileName, setVarFileName] = useState('')
  const livePreviewRef = useRef(null)
  const uploadLocation = orgId ? `${orgId}/__FILENAME__` : ''
  const uploadMs = AppConfig.ENDPOINT

  const templateKeyOptions = useMemo(
    () => templateKeyOptionsFromSettings(orgId, selectedTemplateKey || value.waTemplateKey),
    [orgId, selectedTemplateKey, value.waTemplateKey]
  )

  const selectedTemplateOption = useMemo(() => {
    if (!selectedTemplateKey) return null
    return templateKeyOptions.find((o) => o.key === selectedTemplateKey)
      || { key: selectedTemplateKey, label: selectedTemplateKey, text: selectedTemplateKey }
  }, [selectedTemplateKey, templateKeyOptions])

  const mappingSlots = useMemo(
    () => getWaMappingSlots(waComponents, waTemplate),
    [waComponents, waTemplate]
  )

  const mappingHealth = useMemo(() => {
    if (!selectedTemplateKey || hydrateError) {
      return { broken: false, reason: null }
    }
    return getWaMappingHealth({
      waTemplateKey: selectedTemplateKey,
      waTemplate,
      waComponents,
    })
  }, [selectedTemplateKey, waTemplate, waComponents, hydrateError])

  const applyTemplateVariables = (vars) => {
    setTemplateVariables(contactVariableOptions(vars))
  }

  const applyPreviewResponse = (preview, selectedKey, previousBindings = {}) => {
    livePreviewRef.current = preview || null
    if (!preview) {
      setWaTemplate(null)
      setWaComponents({})
      setHydrateError('WhatsApp template details could not be loaded for this key.')
      return
    }

    const hydrateFailed = Boolean(preview._prereqWarning || preview._waHydrateError || preview._waKeyBroken)
    if (hydrateFailed || !preview.waTemplate) {
      setWaTemplate(null)
      setWaComponents({})
      setMappingsDirty(false)
    } else {
      setWaTemplate(preview.waTemplate || null)
      const skeleton = normalizeWaComponentsMap(preview.waComponents || {})
      const previous = normalizeWaComponentsMap(previousBindings)
      const remapped = remapWaComponentsToTemplate(
        preview.waTemplate,
        Object.keys(previous).length > 0 ? previous : skeleton,
        skeleton
      )
      setWaComponents(remapped)
      setMappingsDirty(!waComponentsMapsEqual(previous, remapped))
    }

    const nextHydrateError = preview._waHydrateError
      || (preview._waKeyBroken
        ? (preview._waKeyBrokenReason || buildUnmappedTemplateKeyReason(selectedKey || preview.waTemplateKey || ''))
        : null)
      || (
        !preview.waTemplate
        && !Object.keys(normalizeWaComponentsMap(preview.waComponents || {})).length
          ? (
            preview._waKeyBrokenReason
            || 'WhatsApp template details could not be loaded for this key. '
              + 'Check that the template tag resolves to an approved Meta template on the account WhatsApp number.'
          )
          : null
      )
    setHydrateError(nextHydrateError)
    setPrereqWarning(preview._prereqWarning || null)
    if (!templateVariablesProp && Array.isArray(preview.templateVariables) && preview.templateVariables.length > 0) {
      applyTemplateVariables(preview.templateVariables)
    }
  }

  const fetchWaTemplatePreview = async (key) => {
    return ApiHelper.callAwait({
      ms: AppConfig.CDP_MS.ENDPOINT,
      method: 'POST',
      paths: ['miscs', 'wa-template-preview'],
      queryParams: { orgId },
      jsonBody: { waTemplateKey: key },
    })
  }

  const resolvePreview = async (key) => {
    const cached = cachedWaTemplatePreview(previewByKey, key)
    if (cached) return cached
    return fetchWaTemplatePreview(key)
  }

  const fetchWaCrmNumbers = async () => {
    if (!orgId) {
      setWaCrmNumberMissing(true)
      return
    }
    try {
      const resp = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId, provider: 'WHATSAPP' },
      })
      setWaCrmNumberMissing(isWhatsAppCrmNumbersEmpty(resp))
    } catch (err) {
      setWaCrmNumberMissing(false)
    }
  }

  const hydrateKey = async (key, previousBindings) => {
    if (!key) {
      setWaTemplate(null)
      setWaComponents({})
      setIsLoading(false)
      return
    }
    const cached = cachedWaTemplatePreview(previewByKey, key)
    if (cached) {
      applyPreviewResponse(cached, key, previousBindings || {})
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    try {
      const preview = await resolvePreview(key)
      applyPreviewResponse(preview, key, previousBindings || {})
    } catch (err) {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to load WhatsApp template details'
      setWaTemplate(null)
      setWaComponents({})
      setHydrateError(errMsg)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    const initial = normalizeValue(props.value)
    setSelectedTemplateKey(initial.waTemplateKey)
    setWaTemplate(null)
    setWaComponents({})
    setMappingsDirty(false)
    setWaCrmNumberMissing(false)
    setHydrateError(null)
    setPrereqWarning(null)
    applyTemplateVariables(templateVariablesProp)
    livePreviewRef.current = null
    fetchWaCrmNumbers()
    hydrateKey(initial.waTemplateKey, initial.waComponents)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    applyTemplateVariables(templateVariablesProp)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateVariablesProp])

  const handleTemplateKeyChange = async (newKey) => {
    const key = (newKey || '').trim()
    if (key === (selectedTemplateKey || '')) return

    if (!key) {
      setSelectedTemplateKey('')
      setWaTemplate(null)
      setWaComponents({})
      setMappingsDirty(false)
      setHydrateError(null)
      setPrereqWarning(null)
      livePreviewRef.current = null
      return
    }

    setSelectedTemplateKey(key)
    setMappingsDirty(false)
    setHydrateError(null)
    setPrereqWarning(null)

    const savedKey = value.waTemplateKey
    const previousBindings = key === savedKey ? value.waComponents : {}
    const cached = cachedWaTemplatePreview(previewByKey, key)
    if (cached) {
      applyPreviewResponse(cached, key, previousBindings)
      return
    }

    if (key !== savedKey) {
      setWaTemplate(null)
      setWaComponents({})
    }
    setIsHydrating(true)
    try {
      const preview = await resolvePreview(key)
      applyPreviewResponse(preview, key, previousBindings)
    } catch (err) {
      const errMsg = Helper.getErrorMsg(err) || 'Failed to preview WhatsApp template'
      setWaTemplate(null)
      setWaComponents({})
      setHydrateError(errMsg)
      if (showToast) showToast(errMsg, 'error')
    } finally {
      setIsHydrating(false)
    }
  }

  const openEditVariable = (slot) => {
    const bound = (waComponents && waComponents[slot.id]) || slot || {}
    const currentText = bound.text || ''
    const isMatchingContactVar = templateVariables.some((v) => v.value === currentText)
    if (isMatchingContactVar) {
      setVarRadioValue(currentText)
      setVarCustomText('')
    } else if (currentText) {
      setVarRadioValue('custom')
      setVarCustomText(currentText)
    } else {
      setVarRadioValue(templateVariables[0] ? templateVariables[0].value : 'custom')
      setVarCustomText('')
    }
    setVarMediaLink(bound.mediaLink || '')
    setVarMedia(bound.media || '')
    setVarFileName(bound.fileName || '')
    setEditingVar(slot)
  }

  const handleMediaUploadSuccess = (_uploadId, signedUploadInfo) => {
    const link = (signedUploadInfo && signedUploadInfo.link) || ''
    const assetKey = (signedUploadInfo && signedUploadInfo.fields && signedUploadInfo.fields.key != null)
      ? String(signedUploadInfo.fields.key)
      : ''
    setVarMediaLink(link)
    setVarMedia(assetKey)
    if (editingVar && String(editingVar.format || '').toLowerCase() === 'document' && assetKey && !varFileName) {
      const base = assetKey.split('/').pop() || ''
      setVarFileName(base || 'Untitled')
    }
  }

  const saveVariableEdit = () => {
    if (!editingVar) return
    const slot = editingVar
    const currentBindings = normalizeWaComponentsMap(waComponents)
    const format = String(slot.format || '').toLowerCase()
    const isMedia = Boolean(slot.isMedia) || ['image', 'video', 'document'].includes(format)

    if (isMedia) {
      const hasMedia = Boolean(String(varMediaLink || varMedia || '').trim())
      if (!hasMedia) {
        if (showToast) showToast(`Provide ${format || 'media'} before continuing`, 'error')
        return
      }
      if (format === 'document' && !String(varFileName || '').trim()) {
        setVarFileName('Untitled')
      }
    }

    let textVal = ''
    if (isMedia) {
      textVal = varMediaLink || varMedia || ''
    } else if (varRadioValue === 'custom') {
      textVal = varCustomText
    } else {
      textVal = varRadioValue
    }

    const updated = {
      ...(currentBindings[slot.id] || {}),
      ...slot,
      id: String(slot.id),
      text: textVal,
      mediaLink: isMedia ? varMediaLink : '',
      media: isMedia ? (varMedia || varMediaLink) : '',
      fileName: isMedia && format === 'document'
        ? (String(varFileName || '').trim() || 'Untitled')
        : (varFileName || ''),
    }

    setWaComponents(buildWaComponentsFromBindings(mappingSlots, {
      ...currentBindings,
      [String(slot.id)]: updated,
    }))
    setMappingsDirty(true)
    setEditingVar(null)
  }

  const handleSave = () => {
    const savedKey = value.waTemplateKey
    const pendingUnset = !selectedTemplateKey && Boolean(savedKey)
    const pendingKeyChange = Boolean(selectedTemplateKey) && selectedTemplateKey !== savedKey

    if (!pendingUnset && !pendingKeyChange && !mappingsDirty) {
      onClose()
      return
    }

    if (
      !pendingUnset
      && selectedTemplateKey
      && mappingSlots.length > 0
      && !areAllWaMappingSlotsFilled(mappingSlots, waComponents)
    ) {
      if (showToast) showToast('Map all template variables before saving', 'error')
      return
    }

    if (pendingUnset) {
      if (onSave) onSave({ waTemplateKey: '', waComponents: {} }, null)
      onClose()
      return
    }

    const comps = selectedTemplateKey
      ? buildWaComponentsFromBindings(mappingSlots, normalizeWaComponentsMap(waComponents))
      : {}
    if (onSave) {
      onSave({
        waTemplateKey: selectedTemplateKey,
        waComponents: comps,
      }, livePreviewRef.current)
    }
    onClose()
  }

  const savedKey = value.waTemplateKey
  const pendingUnset = !selectedTemplateKey && Boolean(savedKey)
  const pendingKeyChange = Boolean(selectedTemplateKey) && selectedTemplateKey !== savedKey
  const allVariablesMapped = areAllWaMappingSlotsFilled(mappingSlots, waComponents)
  const hasUnmappedRequiredVars = Boolean(selectedTemplateKey)
    && !hydrateError
    && mappingSlots.length > 0
    && !allVariablesMapped
  const isSaveDisabled = isLoading || isHydrating
    || (!mappingsDirty && !pendingUnset && !pendingKeyChange)
    || hasUnmappedRequiredVars

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='md'
        fullWidth
        disableEnforceFocus
        style={{ zIndex: 13000 }}
        classes={{ paper: css(AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar) }}
        PaperProps={{
          style: {
            maxHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: AppTheme.borderRadius,
          },
        }}
      >
        <DialogTitle
          style={{
            flexShrink: 0,
            borderBottom: '1px solid #E5E7EB',
            padding: '16px 24px',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center' style={{ gap: 10 }}>
              <WhatsAppIcon style={{ color: '#25D366', fontSize: 26 }} />
              <Box>
                <Typography variant='h6' style={{ fontWeight: 700, color: '#111827', fontSize: 18 }}>
                  {title || 'Send Template'}
                </Typography>
                <Typography variant='body2' style={{ color: '#6B7280', fontSize: 12 }}>
                  {subtitle || 'Pick a WhatsApp template key. The mapped template is sent when this step runs.'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size='small' style={{ color: '#6B7280' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            padding: '24px',
            backgroundColor: '#F9FAFB',
          }}
        >
          <Paper
            elevation={0}
            style={{
              padding: 16,
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              backgroundColor: '#FFFFFF',
              marginBottom: 20,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Box display='flex' flexDirection='column' style={{ gap: 12, width: '100%' }}>
              <FormControl fullWidth>
                <div className={css(FormStyles.labelContainer)}>
                  <Typography variant='body2' style={{ fontWeight: 600, color: '#222222' }}>
                    WhatsApp Template Key
                  </Typography>
                  <Tooltip title='Pick a WhatsApp template key to send in this step'>
                    <i
                      className='material-icons-outlined'
                      style={{ fontSize: 14, color: '#7d7d7d', cursor: 'pointer', marginLeft: 5, alignSelf: 'center' }}
                    >
                      info
                    </i>
                  </Tooltip>
                </div>
                <Autocomplete
                  fullWidth
                  options={templateKeyOptions}
                  value={selectedTemplateOption}
                  disabled={isHydrating}
                  onChange={(e, val) => {
                    handleTemplateKeyChange((val && val.key) || '')
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                    }
                  }}
                  getOptionLabel={(option) => (option && (option.label || option.text || option.key)) || ''}
                  getOptionSelected={(option, val) => Boolean(option && val && option.key === val.key)}
                  classes={{
                    popupIndicator: css(FormStyles.popupIndicator),
                    popper: css(FormStyles.dialog),
                    option: css(FormStyles.option),
                    clearIndicator: selectedTemplateOption
                      ? css(FormStyles.clearIndicatorAlwaysVisible)
                      : css(FormStyles.clearIndicatorHidden),
                  }}
                  renderOption={(option) => (
                    <span>{(option && (option.text || option.label || option.key)) || ''}</span>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='standard'
                      placeholder={
                        templateKeyOptions.length > 0
                          ? 'Select template key...'
                          : 'No WhatsApp template keys configured'
                      }
                      className={css(FormStyles.formControl)}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        classes: {
                          root: css([FormStyles.textInputStyles, FormStyles.autoCompleteInputRoot]),
                          input: css(FormStyles.formInputStyle),
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Paper>

          {(isLoading || isHydrating) && (
            <Box display='flex' alignItems='center' justifyContent='center' p={4} style={{ gap: 12 }}>
              <CircularProgress size={24} />
              <Typography variant='body2' color='textSecondary'>
                Loading WhatsApp template preview...
              </Typography>
            </Box>
          )}

          {(waCrmNumberMissing || isMissingDefaultWaNumberError(hydrateError)) && !isHydrating && (
            <Box mb={2}>
              <Alert severity='warning'>
                <span>
                  No WhatsApp number is connected for this account.{' '}
                  <Link
                    to={getCrmNumbersRoute(orgId)}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Connect a WhatsApp number
                  </Link>
                </span>
              </Alert>
            </Box>
          )}
          {hydrateError && !isMissingDefaultWaNumberError(hydrateError) && !isLoading && !isHydrating && (
            <Box mb={2}>
              <Alert severity='warning'>
                {hydrateError}
              </Alert>
            </Box>
          )}
          {prereqWarning && !isLoading && !isHydrating && (
            <Box mb={2}>
              <Alert severity='info'>
                {typeof prereqWarning === 'string' ? prereqWarning : (prereqWarning.message || JSON.stringify(prereqWarning))}
              </Alert>
            </Box>
          )}

          {mappingHealth.broken && !hydrateError && !isLoading && !isHydrating && selectedTemplateKey && (
            <Box mb={2}>
              <Alert severity='warning'>
                {mappingHealth.reason
                  || 'WhatsApp template variables need to be remapped for the current template.'}
                {mappingHealth.formatMismatch
                  ? ' Variable count or type changed — review and remap any cleared fields before saving.'
                  : ''}
              </Alert>
            </Box>
          )}

          {!selectedTemplateKey && !isLoading && !isHydrating && (
            <Box
              p={4}
              textAlign='center'
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: 8,
                border: '1px dashed #D1D5DB',
              }}
            >
              <WhatsAppIcon style={{ fontSize: 40, color: '#9CA3AF', marginBottom: 8 }} />
              <Typography variant='subtitle1' style={{ color: '#4B5563', fontWeight: 600 }}>
                No WhatsApp Template Configured
              </Typography>
              <Typography variant='body2' style={{ color: '#9CA3AF', marginTop: 4 }}>
                Select a WhatsApp template key above to map variables and preview the template.
              </Typography>
            </Box>
          )}

          {selectedTemplateKey && !isLoading && !isHydrating && !waTemplate && mappingSlots.length === 0 && (
            <Box
              p={4}
              textAlign='center'
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: 8,
                border: '1px dashed #D1D5DB',
              }}
            >
              <WhatsAppIcon style={{ fontSize: 40, color: '#9CA3AF', marginBottom: 8 }} />
              <Typography variant='subtitle1' style={{ color: '#4B5563', fontWeight: 600 }}>
                Template details unavailable
              </Typography>
              <Typography variant='body2' style={{ color: '#9CA3AF', marginTop: 4 }}>
                {hydrateError
                  ? 'Fix the WhatsApp template key mapping, or choose another key.'
                  : 'The key is set, but the WhatsApp template and variable mappings could not be loaded. Re-select the key or fix the template tag mapping, then try again.'}
              </Typography>
            </Box>
          )}

          {selectedTemplateKey && !isLoading && !isHydrating && (waTemplate || mappingSlots.length > 0) && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FFFFFF',
                    height: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography variant='subtitle2' style={{ fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                    Template Variables ({mappingSlots.length})
                  </Typography>

                  {mappingSlots.length === 0 ? (
                    <Typography variant='body2' color='textSecondary'>
                      This WhatsApp template has no variable placeholders.
                    </Typography>
                  ) : (
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: 600, fontSize: 12 }}>Index</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: 12 }}>Format</TableCell>
                          <TableCell style={{ fontWeight: 600, fontSize: 12 }}>Value</TableCell>
                          <TableCell align='right' style={{ fontWeight: 600, fontSize: 12 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappingSlots.map((slot, idx) => {
                          const bound = (waComponents && waComponents[slot.id]) || slot || {}
                          const format = String(slot.format || 'text').toLowerCase()
                          const mediaUrl = bound.mediaLink || ''
                          const displayVal = slot.isMedia
                            ? (mediaUrl || bound.media || bound.fileName || '[Media file]')
                            : (bound.text || '[Not set]')

                          return (
                            <TableRow key={slot.id}>
                              <TableCell style={{ fontSize: 12 }}>
                                {idx + 1}
                              </TableCell>
                              <TableCell style={{ fontSize: 12 }}>
                                {(slot.format || 'text').toUpperCase()}
                                {slot.vIndex ? ` {{${slot.vIndex}}}` : ''}
                              </TableCell>
                              <TableCell style={{ fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {slot.isMedia && format === 'image' && mediaUrl ? (
                                  <Tooltip title={displayVal} arrow placement='top'>
                                    <img
                                      src={mediaUrl}
                                      alt={bound.media || 'Media'}
                                      style={{ height: 36, maxWidth: 120, objectFit: 'contain', borderRadius: 4 }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={displayVal} arrow placement='top'>
                                    <span>{displayVal}</span>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell align='right'>
                                <Tooltip title='Edit Variable' placement='top'>
                                  <IconButton
                                    size='small'
                                    color='primary'
                                    onClick={() => openEditVariable(slot)}
                                  >
                                    <EditIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FFFFFF',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='subtitle2' style={{ fontWeight: 700, color: '#1F2937', marginBottom: 12, width: '100%' }}>
                    WhatsApp Preview
                  </Typography>
                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {waTemplate && Array.isArray(waTemplate.components) ? (
                      <WhatsappTemplatePreview
                        components={buildPreviewComponentsFromTemplate(
                          waTemplate.components,
                          waComponents,
                          { hideTemplateExamplesWhenUnset: true }
                        )}
                      />
                    ) : (
                      <Typography variant='body2' color='textSecondary'>
                        Preview will appear after the template is resolved.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions
          style={{
            flexShrink: 0,
            borderTop: '1px solid #E5E7EB',
            padding: '16px 24px',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button onClick={onClose} style={{ textTransform: 'none', color: '#6B7280', fontWeight: 600 }}>
            Cancel
          </Button>
          <AppButton
            buttonStyle={isSaveDisabled ? FormStyles.buttonDisabled : FormStyles.button}
            buttonTextStyle={isSaveDisabled ? FormStyles.buttonDisabledText : FormStyles.buttonText}
            disabled={isSaveDisabled}
            onClick={handleSave}
            buttonText='Save Mappings'
          />
        </DialogActions>
      </Dialog>

      {editingVar && (
        <Dialog
          open={Boolean(editingVar)}
          onClose={() => setEditingVar(null)}
          maxWidth='sm'
          fullWidth
          style={{ zIndex: 13002 }}
          disableEnforceFocus
          classes={{ paper: css(AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar) }}
          PaperProps={{
            style: {
              borderRadius: AppTheme.borderRadius,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle style={{ borderBottom: '1px solid #E5E7EB' }}>
            Edit {editingVar.label || editingVar.id}
          </DialogTitle>
          <DialogContent style={{ paddingTop: 20 }}>
            {editingVar.isMedia ? (
              <Box display='flex' flexDirection='column' style={{ gap: 16 }}>
                {String(editingVar.format || '').toLowerCase() === 'image' ? (
                  <Box>
                    <Typography variant='caption' style={{ fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                      Image (PNG/JPEG, max 1920px height)
                    </Typography>
                    <ImageUploaderWithAssetPicker
                      key={`wa-media-${editingVar.id}-${varMediaLink || 'empty'}`}
                      uploadId={`wa-media-${editingVar.id}`}
                      url={varMediaLink}
                      orgId={orgId}
                      uploadLocation={uploadLocation}
                      uploadMs={uploadMs}
                      uploadType='media'
                      uploadAccept='image/png, image/jpeg'
                      mode='fit'
                      height={1920}
                      borderRadius={0}
                      allowedFormats={WA_IMAGE_ALLOWED_FORMATS}
                      assetPickerZIndex={WA_MEDIA_ASSET_PICKER_Z}
                      onUploadSuccess={handleMediaUploadSuccess}
                    />
                  </Box>
                ) : String(editingVar.format || '').toLowerCase() === 'video' ? (
                  <Box>
                    <Typography variant='caption' style={{ fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                      Video (MP4, max 16MB)
                    </Typography>
                    <DocumentUploader
                      key={`wa-media-${editingVar.id}-${varMedia || varMediaLink || 'empty'}`}
                      label='Video'
                      uploadId={`wa-media-${editingVar.id}`}
                      link={varMediaLink}
                      value={varMedia || varMediaLink}
                      uploadLocation={uploadLocation}
                      uploadMs={uploadMs}
                      uploadType='media'
                      uploadAccept='video/mp4'
                      fileSizeLimit={16000000}
                      onUploadSuccess={handleMediaUploadSuccess}
                    />
                  </Box>
                ) : (
                  <Box>
                    <Typography variant='caption' style={{ fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: 8 }}>
                      Document (PDF, max 20MB)
                    </Typography>
                    <DocumentUploader
                      key={`wa-media-${editingVar.id}-${varMedia || varMediaLink || 'empty'}`}
                      label='Document'
                      uploadId={`wa-media-${editingVar.id}`}
                      link={varMediaLink}
                      value={varMedia || varMediaLink}
                      uploadLocation={uploadLocation}
                      uploadMs={uploadMs}
                      uploadType='media'
                      uploadAccept='application/pdf'
                      fileSizeLimit={20000000}
                      onUploadSuccess={handleMediaUploadSuccess}
                    />
                  </Box>
                )}
                {String(editingVar.format || '').toLowerCase() === 'document' && (
                  <TextField
                    label='File Name'
                    placeholder='e.g. Agreement.pdf'
                    variant='outlined'
                    size='small'
                    fullWidth
                    value={varFileName}
                    onChange={(e) => setVarFileName(e.target.value)}
                    helperText='Name displayed in WhatsApp chat for the document'
                  />
                )}
              </Box>
            ) : (
              <FormControl component='fieldset' fullWidth>
                <FormLabel component='legend' style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Contact Variable or Custom Text
                </FormLabel>
                <RadioGroup
                  value={varRadioValue}
                  onChange={(e) => setVarRadioValue(e.target.value)}
                >
                  {templateVariables.map((v) => (
                    <FormControlLabel
                      key={v.value}
                      value={v.value}
                      control={<Radio color='primary' size='small' />}
                      label={v.label}
                    />
                  ))}
                  <FormControlLabel
                    value='custom'
                    control={<Radio color='primary' size='small' />}
                    label='[Custom Text]'
                  />
                </RadioGroup>
                {varRadioValue === 'custom' && (
                  <Box mt={1}>
                    <TextField
                      label='Custom Value'
                      placeholder='Enter custom text or {{contact.attribute}}'
                      variant='outlined'
                      size='small'
                      fullWidth
                      value={varCustomText}
                      onChange={(e) => setVarCustomText(e.target.value)}
                    />
                  </Box>
                )}
              </FormControl>
            )}
          </DialogContent>
          <DialogActions style={{ borderTop: '1px solid #E5E7EB', padding: 16 }}>
            <Button onClick={() => setEditingVar(null)} style={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={saveVariableEdit}
              style={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#2563EB' }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
